import { Vector3 } from '@babylonjs/core';
import { BabylonjsModule } from '@modules/babylonjs';
import { Injectable } from '@modules/core';
import {
  CameraAnimationOption,
  CameraInputParam,
  CameraPresetTypes,
  CameraTargetSystem,
  CameraType,
  delay,
  DoubleTouchPanning,
  DoubleTouchPinchZoom,
  EasingFunctionType,
  EasingModeType,
  getDeviceInfo,
  getEmptyInput,
  MouseWheelZoom,
  RenderManager,
  SingleTouchPanning,
  SingleTouchRotation,
  toRadian,
  ZEDCameraAPI,
} from '@modules/standard';
import { CommonPick } from '@modules/standard/src/camera/inputs/CommonPick';

import { onCameraViewTypeChanged } from '..';
import { INTERIOR_FOV_DESKTOP, INTERIOR_FOV_MOBILE, MOUSE_ZOOM_SENSIBILITY, ROTATION_SENSIBILITY_X, ROTATION_SENSIBILITY_Y } from '../Constants';
import { GroundSystem } from '../ecs/system/GroundSystem';

import { WindowCameraDolly } from './inputs/WindowCameraDolly';

export type TourDanjiCameraViewType = CameraType | 'DanjiView' | 'Transition' | 'UnitWindowView' | 'FakeUnitWindowView';

const TourDanjiPresetCameraTypes = [...CameraPresetTypes, 'DanjiView', 'UnitWindowView', 'FakeUnitWindowView'];

type CameraViewParam = {
  type: TourDanjiCameraViewType;
  position?: Vector3;
  target?: Vector3;
  betaMin?: number;
  betaMax?: number;
  radiusMin?: number;
  radiusMax?: number;
  up?: Vector3;
  fov?: number;
  checkCollisions?: boolean;
};

type DongEntity = {
  Dong: {
    dongCenter: Vector3;
    dongHeight: number;
  };
};

export class TourDanjiCameraAPI extends ZEDCameraAPI implements Injectable {
  static readonly INJECTABLE_TOKEN = Symbol.for('TourDanjiCameraAPI');

  private readonly groundSystem;
  private cameraViewHistory: CameraViewParam[] = [];
  private getDongEntityCallback: ((buildingId: number) => DongEntity | null) | null = null;

  static _interiorFov: number = INTERIOR_FOV_MOBILE;

  constructor({
    [BabylonjsModule.INJECTABLE_TOKEN]: babylonjs,
    [RenderManager.INJECTABLE_TOKEN]: renderManager,
    [CameraTargetSystem.INJECTABLE_TOKEN]: cameraTargetSystem,
    [GroundSystem.INJECTABLE_TOKEN]: groundSystem,
  }: {
    [BabylonjsModule.INJECTABLE_TOKEN]: BabylonjsModule;
    [RenderManager.INJECTABLE_TOKEN]: RenderManager;
    [CameraTargetSystem.INJECTABLE_TOKEN]: CameraTargetSystem;
    [GroundSystem.INJECTABLE_TOKEN]: GroundSystem;
  }) {
    super(babylonjs.scene, false, renderManager, cameraTargetSystem);
    this.groundSystem = groundSystem;

    this.applyPresetInputs('DanjiView');
    this.bindDanjiExtends();
    this.camera.maxZ = 10000;

    this.camera.alpha = Math.PI / 2;
    this.camera.beta = Math.PI / 2;
    this.camera.radius = 700;
    this.camera.setTarget(new Vector3(0, 1, 0));

    this.camera.fov = 0.43;
    this.camera.lowerRadiusLimit = 300;
    this.camera.upperRadiusLimit = 800;
    this.camera.lowerBetaLimit = Math.PI / 10;
    this.camera.upperBetaLimit = Math.PI / 2;
    this.camera.collisionRadius = new Vector3(10, 10, 10);
    this.camera.checkCollisions = true;
    TourDanjiCameraAPI._interiorFov = getDeviceInfo().isMobileOrTablet ? INTERIOR_FOV_MOBILE : INTERIOR_FOV_DESKTOP;
  }

  public setGetDongEntityCallback(callback: (buildingId: number) => DongEntity | null): void {
    this.getDongEntityCallback = callback;
  }

  public override dispose(): void {
    super.dispose();
  }

  protected override getPresetInputs(cameraType: TourDanjiCameraViewType) {
    switch (cameraType) {
      case 'Default':
      case 'DanjiView':
        return this.getDanjiViewInputs();
      case 'Transition':
        return getEmptyInput();
      case 'UnitWindowView':
        return this.getUnitWindowViewInputs();
      case 'FakeUnitWindowView':
        return this.getFakeUnitWindowViewInputs();
      default:
        return super.getPresetInputs(cameraType);
    }
  }

  public override applyPresetInputs(cameraType: TourDanjiCameraViewType) {
    if (this.cameraType === cameraType) return;

    this.cameraType = <any>cameraType;
    this.attachInputs(this.getPresetInputs(cameraType));
  }

  /**
   * default extends를 특정 floorplan의 extends로 설정합니다
   * @param floorplanId 입력 시 매칭되는 floorplan으로 extends 설정. 매칭되지 않거나 id 입력 안할 시 첫번째 floorplan으로 설정
   */
  public bindDanjiExtends() {
    this.bindGetDefaultExtends(() => {
      this.camera.lowerGroundToDistance = 20;
      return this.groundSystem.extends;
    });
  }
  public override getPresetCameraTypes() {
    return TourDanjiPresetCameraTypes;
  }

  public pushCameraView(param: CameraViewParam, animationOption?: Partial<CameraAnimationOption>): Promise<void> {
    const previousCameraView = {
      type: this.cameraType,
      position: this.camera.position.clone(),
      target: this.camera.target.clone(),
      betaMin: this.camera.lowerBetaLimit,
      betaMax: this.camera.upperBetaLimit,
      radiusMin: this.camera.lowerRadiusLimit,
      radiusMax: this.camera.upperRadiusLimit,
      up: this.camera.upVector.clone(),
      fov: this.camera.fov,
      checkCollisions: this.camera.checkCollisions,
    } as CameraViewParam;

    this.cameraViewHistory.push(previousCameraView);

    return this.changeCameraView(param, animationOption);
  }

  public async popCameraView(animationOption?: Partial<CameraAnimationOption>): Promise<void> {
    const newCameraPosition = this.cameraViewHistory.pop();
    if (!newCameraPosition) return Promise.resolve();
    
    const currentCheckCollisions = this.camera.checkCollisions;    
    this.camera.checkCollisions = false;
    const restoreCheckCollisions = () => {
      this.camera.checkCollisions = newCameraPosition.checkCollisions ?? currentCheckCollisions;
    };
    const transitionPromise = this.changeCameraView({...newCameraPosition, checkCollisions: false}, animationOption);
    
    transitionPromise.then(() => {
      restoreCheckCollisions();
    });
    
    return transitionPromise;
  }

  private changeCameraView(param: CameraViewParam, animationOption?: Partial<CameraAnimationOption>): Promise<void> {
    const { type, position, target, betaMin, betaMax, radiusMin, radiusMax, up, fov, checkCollisions } = param;

    this.applyPresetInputs(type);

    Object.assign(this.camera, {
      lowerRadiusLimit: radiusMin ?? this.camera.lowerRadiusLimit,
      upperRadiusLimit: radiusMax ?? this.camera.upperRadiusLimit,
      lowerBetaLimit: betaMin ?? this.camera.lowerBetaLimit,
      upperBetaLimit: betaMax ?? this.camera.upperBetaLimit,
      upVector: up ?? this.camera.upVector,
      checkCollisions: checkCollisions ?? this.camera.checkCollisions,
    });

    onCameraViewTypeChanged?.notifyObservers(type);

    return this.camera.transition(
      position ?? this.camera.position,
      target ?? this.camera.target,
      fov ?? this.camera.fov,
      animationOption
    );
  }

  public getCurrentCameraViewType() {
    return this.cameraType?.toString();
  }

  /**
   * Moves the camera to focus on a specific dong (building)
   * @param buildingId The ID of the building to focus on
   * @param animationOption Optional animation settings for the camera movement
   * @returns A promise that resolves when the camera movement is complete
   */
  public async focusOnDong(
    buildingId: number,
    animationOption?: Partial<CameraAnimationOption>
  ): Promise<void> {
    // Check if the callback is set
    if (!this.getDongEntityCallback) {
      console.warn('Dong entity callback not set. Call setGetDongEntityCallback first.');
      return;
    }
    
    // Get the dong entity using the callback
    const dongEntity = this.getDongEntityCallback(buildingId);
    
    if (!dongEntity) {
      console.warn(`Dong with buildingId ${buildingId} not found`);
      return;
    }

    // Get the dong center position
    const dongCenter = dongEntity.Dong.dongCenter;
    
    // Calculate a good viewing position
    // Position the camera at a distance from the dong center
    const distance = 400; // Adjust this value based on the dong size
    const height = dongEntity.Dong.dongHeight * 0.8; // Position at 70% of the building height
    
    // Create a target position slightly above the ground
    const targetPosition = new Vector3(dongCenter.x, height, dongCenter.z);
    
    // Create a camera position that looks at the target from a distance
    const cameraPosition = targetPosition.add(new Vector3(0, distance, -distance));
    
    // Set up the camera view parameters
    const viewParam: CameraViewParam = {
      type: 'DanjiView',
      position: cameraPosition,
      target: targetPosition,
    };
    
    // Apply the camera view with animation
    return this.changeCameraView(viewParam, animationOption);
  }

  public transitionToUnitWindowView = async (
    position: Vector3,
    direction: Vector3,
    viewType: 'UnitWindowView' | 'FakeUnitWindowView'
  ) => {
    const cameraType = this.cameraType?.toString();
    if (cameraType === 'DanjiView') {
      const targetPosition = position;
      const lookPosition = targetPosition.add(direction.scale(0.01));
      const enterPosition = position.add(direction.scale(5));

      const firstOption: CameraAnimationOption = {
        easingFunction: EasingFunctionType.SineEase,
        easingMode: EasingModeType.In,
        seconds: 0.5,
      };
      const secondOption: CameraAnimationOption = {
        easingFunction: EasingFunctionType.SineEase,
        easingMode: EasingModeType.Out,
        seconds: 0.5,
      };
      await delay(700);
      await this.pushCameraView(
        {
          type: 'Transition',
          position: enterPosition,
          target: lookPosition,
          checkCollisions: false,
        },
        firstOption
      );
      await this.changeCameraView(
        {
          type: 'Transition',
          position: targetPosition,
          radiusMin: 0,
          checkCollisions: false,
        },
        secondOption
      );
      await delay(100);
      await this.changeCameraView({
        type: viewType,
        betaMax: 150 * toRadian,
        radiusMin: 0,
        radiusMax: 0,
        fov: TourDanjiCameraAPI._interiorFov,
      });
    }
  };

  getDanjiViewInputs = () => {
    MouseWheelZoom.option = {
      mouseButton: 'all',
      mouseKeyCode: 'none',
      inertia: 0.7,
      sensibility: MOUSE_ZOOM_SENSIBILITY,
      radiusDampening: 0,
    };
    SingleTouchRotation.option = {
      mouseButton: 'left',
      mouseKeyCode: 'none',
      alpha: true,
      beta: true,
      inertia: 0.7,
      sensibilityX: ROTATION_SENSIBILITY_X,
      sensibilityY: ROTATION_SENSIBILITY_Y,
    };
    SingleTouchPanning.option = { mouseButton: 'right', mouseKeyCode: 'none' };
    return {
      desktop: [SingleTouchPanning, SingleTouchRotation, MouseWheelZoom, CommonPick],
      mobile: [SingleTouchRotation, DoubleTouchPanning, DoubleTouchPinchZoom, CommonPick, MouseWheelZoom],
    } as CameraInputParam;
  };

  getUnitWindowViewInputs = () => {
    SingleTouchRotation.option = {
      mouseButton: 'left',
      mouseKeyCode: 'none',
      alpha: true,
      beta: true,
      inertia: 0.7,
      sensibilityX: -ROTATION_SENSIBILITY_X,
      sensibilityY: -ROTATION_SENSIBILITY_Y,
    };
    SingleTouchPanning.option = { mouseButton: 'right', mouseKeyCode: 'none' };
    return {
      desktop: [SingleTouchRotation],
      mobile: [SingleTouchRotation],
    } as CameraInputParam;
  };

  getFakeUnitWindowViewInputs = () => {
    return {
      desktop: [WindowCameraDolly],
      mobile: [WindowCameraDolly],
    } as CameraInputParam;
  };
}
